import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box } from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import "./ExitUsers.css";
import ExitHeader from "../../../components/ExitUsers/Header/ExitUsersHeader";
import Input from "../../../components/ExitUsers/Input/ExitUserInput";
import PeopleFilters from "../../../components/common/PL/Filters/PeopleFilters";
import GuardiaTable, { GuardiaTableHandle } from "../../../components/common/PL/Table/GuardiaTable";
import GuardiaVehicleTable from "../../../components/common/PL/Table/GuardiaVehicleTable";
import PeopleInsideViewToggle, {
  usePeopleInsideView,
} from "../../../components/common/PL/Toggle/PeopleInsideViewToggle";
import { usePeopleInside } from "../RegistroIngreso/hooks/usePeopleInside";
import { useVehiclesInside } from "../RegistroIngreso/hooks/useVehiclesInside";
import { useLogisticParkNavigation } from "../RegistroIngreso/hooks/useLogisticParkNavigation";
import { useDelayedState } from "../../../hooks/useDelayedState";
import ExitUserScanModal from "./ExitUserScanModal";
import usePLScannerListener from "../hooks/usePLScannerListener";
import { ISearchUser } from "../../../store/types/userTypes";
import { useStore } from "../../../hooks/useStore";
import { useRegistroIngresoSession } from "../RegistroIngreso/session/RegistroIngresoSessionContext";
import {
  processRegistroIngresoScan,
  finalizeBuildingMismatchScan,
  PendingPassScanData,
} from "../RegistroIngreso/utils/processScan";
import ScanLoadingOverlay from "../../../components/common/PL/ScanLoadingOverlay/ScanLoadingOverlay";
import DialogConfirmation from "../../../components/common/PL/Dialog/DialogConfirmation";
import UserSDK from "../../../passapp-sdk/user";

/** El buscador acepta nombre, documento o patente: sólo cuando lo tipeado es puro número asumimos
 * que es un DNI y nos salteamos el modal de "Registrar salida sin ingreso previo" — una patente
 * siempre tiene letras y un nombre no es numérico, así que no hay ambigüedad real. */
const looksLikeDni = (value: string): boolean => /^\d+$/.test(value.trim());

const PAGE_SIZE = 10;

const ExitUsers = () => {
  const { view, setView } = usePeopleInsideView();
  const [search, setSearch] = useState("");
  // Una página por vista: los dos listados tienen totales distintos, así que compartir el número
  // dejaría la paginación apuntando a un resultado que no existe al togglear.
  const [page, setPage] = useState(0);
  const [vehiclePage, setVehiclePage] = useState(0);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [selectedAuthorizationTags, setSelectedAuthorizationTags] = useState<string[]>([]);
  const [selectedTransportCompanies, setSelectedTransportCompanies] = useState<string[]>([]);
  // Carrocería: `getVehiclesInside` no la filtra, así que la aplica la tabla sobre la página cargada.
  const [selectedVehicleType, setSelectedVehicleType] = useState("");
  // Personas se pide siempre: el contador del header es ocupación de personas y no debe cambiar
  // de significado según la vista.
  const { peopleInside, isLoading, total, refetch: refetchPeopleInside } = usePeopleInside({
    search,
    page,
    limit: PAGE_SIZE,
    companies: selectedCompanies,
    authorizationTags: selectedAuthorizationTags,
    transportCompanies: selectedTransportCompanies,
  });
  const {
    vehiclesInside,
    isLoading: isLoadingVehicles,
    total: vehiclesTotal,
    hasTotal: vehiclesHaveTotal,
    refetch: refetchVehiclesInside,
  } = useVehiclesInside({
    search,
    page: vehiclePage,
    limit: PAGE_SIZE,
    companies: selectedCompanies,
    transportCompanies: selectedTransportCompanies,
    enabled: view === "vehicle",
  });
  const { navigateToManualExit } = useLogisticParkNavigation();
  const resetPages = () => {
    setPage(0);
    setVehiclePage(0);
  };

  const { state: filterText, setState: setFilterText } = useDelayedState<string>("", (value) => {
    setSearch(value);
    resetPages();
  });

  const guardiaTableRef = useRef<GuardiaTableHandle>(null);

  const handleCompaniesChange = (values: string[]) => {
    setSelectedCompanies(values);
    resetPages();
  };

  const handleAuthorizationTagsChange = (values: string[]) => {
    setSelectedAuthorizationTags(values);
    resetPages();
  };

  const handleTransportCompaniesChange = (values: string[]) => {
    setSelectedTransportCompanies(values);
    resetPages();
  };
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);

  const history = useHistory();
  const session = useRegistroIngresoSession();
  const { authStore } = useStore();
  const isScanningRef = useRef(false);
  const [isScanning, setIsScanning] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [pendingPassScanData, setPendingPassScanData] = useState<PendingPassScanData | null>(null);

  // Ocupación del complejo: siempre personas, independientemente de la vista activa.
  const totalInside = total;
  const isVehicleView = view === "vehicle";
  // Sin `total` real en la respuesta de vehículos no se puede paginar: se oculta el paginador.
  const pageCount = isVehicleView
    ? vehiclesHaveTotal
      ? Math.ceil(vehiclesTotal / PAGE_SIZE)
      : 0
    : Math.ceil(total / PAGE_SIZE);
  const activePage = isVehicleView ? vehiclePage : page;
  const setActivePage = isVehicleView ? setVehiclePage : setPage;

  /**
   * Si el guardia ya tipeó el DNI en el buscador, saltear el modal de escaneo/carga manual y
   * resolver directo, igual que hace `ExitUserScanModal.handleManualSearch` con su propio input.
   * Sin DNI tipeado, sigue el flujo de siempre.
   */
  const handleRegisterExitWithoutCheckin = async () => {
    const trimmedDni = filterText.trim();
    if (!looksLikeDni(trimmedDni)) {
      setIsScanModalOpen(true);
      return;
    }

    setIsScanning(true);
    try {
      const userSdk = new UserSDK();
      const response = await userSdk.findUserByDNI(authStore.token, trimmedDni, "ARG");
      if (response && response.data) {
        handleScanFound(response.data, trimmedDni, "ARG");
      } else {
        handleScanNotFound(trimmedDni, "ARG");
      }
    } catch (error) {
      console.error("Error searching user for exit:", error);
      handleScanNotFound(trimmedDni, "ARG");
    } finally {
      setIsScanning(false);
    }
  };

  /**
   * Atajo: Enter en el buscador dispara lo mismo que ya hace el click, sin que el guardia tenga
   * que ir hasta la fila o el botón. Con coincidencia entre las personas dentro, es la misma
   * acción que tocar su fila (abre `PersonExitDialog` vía `GuardiaTable`, saltea el chequeo de
   * DNI); sin coincidencia, es la misma que clickear "Registrar salida sin ingreso previo". Sólo
   * aplica en la vista de personas (en la de vehículos no hay una fila de persona para disparar)
   * y con algo tipeado: sin texto, `peopleInside` es la página sin filtrar y "seleccionar la
   * primera" sería un resultado arbitrario que el guardia no buscó.
   */
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || view !== "person" || !filterText.trim()) return;
    if (peopleInside.length > 0) {
      guardiaTableRef.current?.triggerExit(peopleInside[0]);
    } else {
      handleRegisterExitWithoutCheckin();
    }
  };

  const handleScanFound = (userData: any, dni: string, country: string) => {
    setIsScanModalOpen(false);
    navigateToManualExit({
      id: userData.id,
      documentCountry: country,
      documentNumber: dni,
      firstName: userData.firstname || "",
      lastName: userData.lastname || "",
      email: userData.email || "",
      phone: userData.phone || "",
      visitorPhoto: userData.facePhoto || "",
      frontDNI: userData.frontDNI || "",
      backDNI: userData.backDNI || "",
      passId: userData.passId,
      passType: userData.passType,
      passGuestsType: userData.passGuestsType,
      isBuildingMismatch: userData.isBuildingMismatch,
    });
  };

  const handleScanNotFound = (dni: string, country: string) => {
    setIsScanModalOpen(false);
    navigateToManualExit({
      documentCountry: country,
      documentNumber: dni,
    });
  };

  const handleScanSkip = () => {
    setIsScanModalOpen(false);
    navigateToManualExit();
  };

  // HID 2D scanner listener — always active on this screen.
  // Mirrors Home.tsx but with mode: "exit". The RegistroIngreso form in exit mode
  // already handles both cases: user with previous entry and user without.
  const handleHidScan = async (results: ISearchUser) => {
    if (isScanningRef.current) return;
    isScanningRef.current = true;
    setIsScanning(true);

    await processRegistroIngresoScan({
      results,
      mode: "exit",
      intent: "append-tab",
      session,
      authStore,
      history,
      onBuildingMismatch: (pending) => {
        setIsScanning(false);
        setPendingPassScanData(pending);
        setBuildingDialogOpen(true);
      },
      onError: (message) => {
        setIsScanning(false);
        setErrorMessage(message);
        setErrorDialogOpen(true);
        isScanningRef.current = false;
      },
    });
  };

  usePLScannerListener({ enabled: true, onScan: handleHidScan });

  const handleConfirmBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    if (pendingPassScanData) {
      finalizeBuildingMismatchScan({
        pending: pendingPassScanData,
        mode: "exit",
        intent: "append-tab",
        session,
        history,
      });
      setPendingPassScanData(null);
    }
  };

  const handleCancelBuildingMismatch = () => {
    setBuildingDialogOpen(false);
    setPendingPassScanData(null);
    isScanningRef.current = false;
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    isScanningRef.current = false;
  };

  return (
    <div className="container">
      <ExitHeader totalInside={totalInside} capacity={200} />
      <Input
        value={filterText}
        onChange={setFilterText}
        onRegisterExitWithoutCheckin={handleRegisterExitWithoutCheckin}
        onKeyDown={handleSearchKeyDown}
      />
      <PeopleFilters
        selectedCompanies={selectedCompanies}
        onCompaniesChange={handleCompaniesChange}
        selectedAuthorizationTags={selectedAuthorizationTags}
        onAuthorizationTagsChange={handleAuthorizationTagsChange}
        selectedTransportCompanies={selectedTransportCompanies}
        onTransportCompaniesChange={handleTransportCompaniesChange}
        vehicleTypeMode={view === "vehicle"}
        selectedVehicleType={selectedVehicleType}
        onVehicleTypeChange={setSelectedVehicleType}
        trailing={<PeopleInsideViewToggle value={view} onChange={setView} />}
      />
      {view === "person" ? (
        <GuardiaTable
          ref={guardiaTableRef}
          rows={peopleInside}
          isLoading={isLoading}
          onDockUpdated={refetchPeopleInside}
        />
      ) : (
        <GuardiaVehicleTable
          rows={vehiclesInside}
          vehicleTypeFilter={selectedVehicleType}
          isLoading={isLoadingVehicles}
          onDockUpdated={refetchVehiclesInside}
        />
      )}

      {pageCount > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            color="primary"
            count={pageCount}
            page={activePage + 1}
            onChange={(_, value) => setActivePage(value - 1)}
          />
        </Box>
      )}

      <ExitUserScanModal
        open={isScanModalOpen}
        onClose={() => setIsScanModalOpen(false)}
        onFound={handleScanFound}
        onNotFound={handleScanNotFound}
        onSkip={handleScanSkip}
      />

      <ScanLoadingOverlay open={isScanning} />
      <DialogConfirmation
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        onConfirm={handleErrorDialogClose}
        title="Error"
        message={errorMessage}
        confirmLabel="Aceptar"
        cancelLabel="Cancelar"
      />
      <DialogConfirmation
        open={buildingDialogOpen}
        onClose={handleCancelBuildingMismatch}
        onConfirm={handleConfirmBuildingMismatch}
        title="Advertencia"
        message="Esta autorización es para otro establecimiento"
        confirmLabel="Continuar"
        cancelLabel="Cancelar"
      />
    </div>
  );
};

export default ExitUsers;
